import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, JobListingCard, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    intl,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  // Split listings into regular and job buckets so they render in separate grids.
  // A listing is treated as a job when categoryLevel1 OR listingType equals 'job'.
  const isJobListing = l => {
    const { categoryLevel1, listingType } = l?.attributes?.publicData || {};
    return categoryLevel1 === 'job' || listingType === 'job';
  };
  const regularListings = listings.filter(l => !isJobListing(l));
  const rawJobListings = listings.filter(isJobListing);

  // Client-side sort for job listings when sort=[-]pub_urgent is selected.
  // This ensures urgent jobs always float to the top regardless of API indexing.
  const sortParam = search?.sort || '';
  const jobListings = sortParam.replace(/^-/, '') === 'pub_urgent'
    ? [...rawJobListings].sort((a, b) => {
        const aVal = Number(a?.attributes?.publicData?.urgent) || 0;
        const bVal = Number(b?.attributes?.publicData?.urgent) || 0;
        // '-pub_urgent' = descending (urgent=1 first); 'pub_urgent' = ascending (urgent=0 first)
        return sortParam.startsWith('-') ? bVal - aVal : aVal - bVal;
      })
    : rawJobListings;

  return (
    <div className={classes}>
      {/* Regular listings – original multi-column grid */}
      {regularListings.length > 0 ? (
        <ul className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
          {regularListings.map(l => (
            <li key={l.id.uuid} className={css.resultItem}>
              <ListingCard
                className={css.listingCard}
                listing={l}
                renderSizes={cardRenderSizes(isMapVariant)}
                setActiveListing={setActiveListing}
              />
            </li>
          ))}
          {props.children}
        </ul>
      ) : null}

      {/* Job listings – compact 2-column grid */}
      {jobListings.length > 0 ? (
        <ul className={css.listingCardsJobVariant}>
          {jobListings.map(l => (
            <li key={l.id.uuid} className={css.resultItemJob}>
              <JobListingCard
                className={css.jobListingCard}
                listing={l}
              />
            </li>
          ))}
        </ul>
      ) : null}

      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
